import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { useOktaQuery, Game, GameReport } from '../../../data';
import PitchExecutionPitcher from './PitchExecutionPitcher';
import PitchExecutionFilters from './PitchExecutionFilters';

const StyledPitchReview = styled.div`
    font-size: 12px;
    > div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        > div {
            display: flex;
            align-items: baseline;
            > label {
                margin: 0 5px 0 15px;
                &:first-of-type {
                    margin-left: 0;
                }
            }
        }
    }
`;

const PitchExecution = ({ playerid, gameid, teamid, vsteamid, yearid, isCatcher, isInBats, venueid }) => {
    const { t: translate } = useTranslation();
    const [pitchFilter, setPitchFilter] = useState({});
    const { data, isLoading: arePitchesLoading } = useOktaQuery(['batteries', gameid, teamid], (t) => Game.batteries(t, gameid, { teamid }));
    const { data: pitchReviews, isLoading: isReviewLoading } = useOktaQuery(
        ['pitch-review', gameid],
        (t) => GameReport.queryCatcherReviewPitch(t, gameid),
    );

    const playerPitches = isCatcher
        ? data?.pitchbypitch.filter((p) => p.catcherid === playerid)
        : data?.pitchbypitch.filter((p) => p.pitcherid === playerid);
    const filteredPitches = playerPitches?.filter((p) => {
        let isIncluded = true;
        if (pitchFilter.batterid && p.batterid !== pitchFilter.batterid) isIncluded = false;
        if (pitchFilter.pitcherid && p.pitcherid !== pitchFilter.pitcherid) isIncluded = false;
        if (pitchFilter.inning && p.inning !== pitchFilter.inning) isIncluded = false;
        return isIncluded;
    });

    const innings = playerPitches ? [...new Set(playerPitches.map((p) => p.inning))].sort() : [];
    const onInningChange = (addInnAmt) => setPitchFilter((f) => ({ ...f, inning: pitchFilter.inning + addInnAmt }));

    return (
        <StyledPitchReview>
            {!!playerPitches?.length && (
                <PitchExecutionFilters
                    pitches={playerPitches}
                    onFilterChange={setPitchFilter}
                    isCatcher={isCatcher}
                    value={pitchFilter}
                />
            )}
            <PitchExecutionPitcher
                pitcherId={pitchFilter.pitcherid}
                vsTeamId={vsteamid}
                teamId={teamid}
                gameid={gameid}
                pitchReviews={pitchReviews}
                pitches={filteredPitches}
                arePitchesLoading={arePitchesLoading}
                isReviewLoading={isReviewLoading}
                flaggedPitchFilter={pitchFilter.review}
                yearid={yearid}
                isInBats={isInBats}
                venueId={venueid}
            />
            {!!pitchFilter.inning && innings.length > 1 && (
                <div>
                    <div>
                        {pitchFilter.inning > innings[0] && (
                            <Button variant="link" onClick={() => onInningChange(-1)}>&lt;&nbsp;{translate('common.abbr-previous')}</Button>
                        )}
                    </div>
                    <div>
                        {pitchFilter.inning < innings[innings.length - 1] && (
                            <Button variant="link" onClick={() => onInningChange(1)}>{translate('common.next')}&nbsp;&gt;</Button>
                        )}
                    </div>
                </div>
            )}
        </StyledPitchReview>
    );
};

PitchExecution.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    teamid: PropTypes.number.isRequired,
    vsteamid: PropTypes.number.isRequired,
    yearid: PropTypes.number.isRequired,
    isCatcher: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    isInBats: PropTypes.bool,
    venueid: PropTypes.number.isRequired,
};
PitchExecution.defaultProps = {
    isCatcher: false,
    isInBats: false,
};

export default PitchExecution;
